import { useEffect, useRef } from 'react';
import ambient1 from '../assets/ambient/samples/1.webp';
import ambient2 from '../assets/ambient/samples/2.webp';
import ambient3 from '../assets/ambient/samples/3.webp';
import ambient4 from '../assets/ambient/samples/4.webp';
import ambient5 from '../assets/ambient/samples/5.webp';
import ambient6 from '../assets/ambient/samples/6.webp';

const IMAGES = [ambient1, ambient2, ambient3, ambient4, ambient5, ambient6];
const COLORS = [[125, 120, 183], [100, 143, 175], [100, 163, 156], [189, 148, 114], [183, 126, 150], [123, 132, 150]];
const STAR_INTERVAL = 4000;
const STAR_FADE = 750;
const RIPPLE_DURATION = 1200;
const RIPPLE_AMPLITUDE = 8.4;
const TAU = Math.PI * 2;
const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const mix = (a, b, amount) => a + (b - a) * amount;
const smooth = (start, end, value) => {
  const t = clamp((value - start) / (end - start));
  return t * t * (3 - 2 * t);
};
const random = (seed) => {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
};

// Every curve has its own entrance, direction and destination. There is no hub.
const PATHS = [
  [-0.1, 0.14, 0.24, -0.08, 0.62, 0.46, 1.1, 0.22],
  [-0.1, 0.47, 0.22, 0.06, 0.6, 0.89, 1.1, 0.61],
  [-0.1, 0.85, 0.35, 0.55, 0.73, 1.13, 1.1, 0.77],
  [0.08, -0.1, 0.44, 0.28, -0.19, 0.66, 0.25, 1.1],
  [0.42, -0.1, 0.1, 0.25, 0.69, 0.79, 0.43, 1.1],
  [0.77, -0.1, 0.45, 0.35, 1.09, 0.65, 0.78, 1.1],
  [-0.1, 0.31, 0.27, 0.66, 0.44, -0.18, 0.69, -0.1],
  [0.2, 1.1, 0.32, 0.77, 0.93, 0.69, 1.1, 0.29],
  [1.1, 0.05, 0.74, 0.43, 0.32, 0.2, -0.1, 0.66],
  [0.63, 1.1, 0.97, 0.73, 0.08, 0.65, -0.1, 0.96],
  [0.93, -0.1, 1.18, 0.43, 0.49, 0.54, 0.91, 1.1],
  [-0.1, 0.03, 0.19, 0.26, 0.09, 0.85, 0.52, 1.1],
  [-0.1, 0.69, 0.24, 0.92, 0.59, 0.29, 1.1, 0.4],
  [0.16, -0.1, 0.12, 0.12, 0.68, 0.41, 1.1, 0.02],
  [1.1, 0.94, 0.88, 0.56, 0.3, 0.7, -0.1, 0.39],
  [0.55, -0.1, 0.95, 0.09, 0.36, 0.69, 0.05, 1.1],
  [-0.1, 0.57, 0.27, 0.4, 0.36, 0.05, 0.31, -0.1],
  [0.99, 1.1, 0.64, 0.93, 0.8, 0.45, 1.1, 0.52],
];

function trajectory(t, lane, scroll) {
  const p = PATHS[lane];
  const u = 1 - t;
  const wave = Math.sin(t * TAU * 1.5 + lane * 1.7 + scroll * 4) * Math.sin(t * Math.PI);
  return {
    x: u ** 3 * p[0] + 3 * u * u * t * p[2] + 3 * u * t * t * p[4] + t ** 3 * p[6] + wave * 0.022,
    y: u ** 3 * p[1] + 3 * u * u * t * p[3] + 3 * u * t * t * p[5] + t ** 3 * p[7] + wave * 0.025,
  };
}

function loadSample(source) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const sample = document.createElement('canvas');
      sample.width = image.naturalWidth;
      sample.height = image.naturalHeight;
      const context = sample.getContext('2d', { willReadFrequently: true });
      if (!context) { resolve(null); return; }
      context.drawImage(image, 0, 0);
      resolve({ width: sample.width, height: sample.height, pixels: context.getImageData(0, 0, sample.width, sample.height).data });
    };
    image.onerror = () => resolve(null);
    image.src = source;
  });
}

export default function TrajectoryBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!context) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(pointer: fine)');
    let disposed = false;
    let width = 1;
    let height = 1;
    let frame = 0;
    let lastTime = 0;
    let activeUntil = performance.now() + 4800;
    let clock = 0;
    let starClock = 0;
    let scroll = 0;
    let targetScroll = 0;
    let streams = 0;
    let targetStreams = 0;
    let stars = 0;
    let targetStars = 0;
    let publicationsTop = 0;
    let aboutTop = 0;
    let scrollRange = 1;
    let samples = [];
    let imageDots = [];
    let ripples = [];
    let pointer = null;
    let lastPointerTime = -Infinity;

    const readScroll = () => {
      const y = window.scrollY;
      const start = Math.max(120, publicationsTop - height * 0.62);
      targetStreams = smooth(start, Math.max(start + 160, publicationsTop - height * 0.24), y);
      targetStars = smooth(aboutTop - height * 0.66, aboutTop - height * 0.28, y);
      targetScroll = clamp(y / scrollRange);
      if (reducedMotion.matches) {
        targetStreams = targetStreams > 0.5 ? 1 : 0;
        targetStars = targetStars > 0.5 ? 1 : 0;
        streams = targetStreams;
        stars = targetStars;
        targetScroll = 0;
        scroll = 0;
      }
      canvas.dataset.scene = targetStars > 0.5 ? 'stars' : targetStreams > 0.5 ? 'streams' : 'images';
    };

    const buildImageDots = () => {
      // Sample tiny local copies; visitors never download the 30 MB originals.
      const fieldWidth = width;
      const fieldHeight = height;
      const spacing = width < 760 ? 12 : 15;
      const columns = Math.ceil(width / spacing);
      const rows = Math.ceil(height / spacing);
      imageDots = [];
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < columns; col += 1) {
          const x = (col + 0.5) * spacing;
          const y = (row + 0.5) * spacing;
          const colors = samples.map((sample) => {
            const scale = Math.max(fieldWidth / sample.width, fieldHeight / sample.height);
            const sx = Math.floor(clamp((x - fieldWidth / 2) / scale + sample.width / 2, 0, sample.width - 1));
            const sy = Math.floor(clamp((y - fieldHeight / 2) / scale + sample.height / 2, 0, sample.height - 1));
            const index = (sy * sample.width + sx) * 4;
            const [r, g, b] = sample.pixels.subarray(index, index + 3);
            const light = (Math.max(r, g, b) * 0.75 + (r + g + b) / 12) / 255;
            return { r, g, b, light };
          });
          imageDots.push({ x, y, colors, phase: random(row * columns + col) * TAU });
        }
      }
    };

    const circle = (x, y, radius, alpha, color, hollow = false) => {
      context.globalAlpha = alpha;
      context.beginPath();
      context.arc(x, y, radius, 0, TAU);
      if (hollow) {
        context.strokeStyle = color;
        context.lineWidth = 0.8;
        context.stroke();
      } else {
        context.fillStyle = color;
        context.fill();
      }
    };

    const ripplePoint = (point, time, strength = 1) => {
      if (!ripples.length || reducedMotion.matches || strength === 0) return point;
      let offsetX = 0;
      let offsetY = 0;
      let totalWeight = 0;
      for (const ripple of ripples) {
        const age = time - ripple.started;
        const dx = point.x - ripple.x;
        const dy = point.y - ripple.y;
        const distance = Math.hypot(dx, dy) || 1;
        const front = distance - age * 0.2;
        const envelope = smooth(0, 120, age) * (1 - smooth(250, RIPPLE_DURATION, age));
        const weight = Math.exp(-(front * front) / 6500) * envelope;
        offsetX += dx / distance * weight;
        offsetY += dy / distance * weight;
        totalWeight += weight;
      }
      // Blend pulses along the cursor path without amplifying their overlap.
      const force = RIPPLE_AMPLITUDE * strength / Math.max(1, totalWeight);
      point.x += offsetX * force;
      point.y += offsetY * force;
      return point;
    };

    const drawImages = (time, alpha) => {
      const phase = reducedMotion.matches ? 0 : clock / 4.6;
      const index = Math.floor(phase) % Math.max(samples.length, 1);
      const next = (index + 1) % Math.max(samples.length, 1);
      const morph = smooth(0.24, 0.94, phase % 1);
      canvas.dataset.image = String(index + 1);
      for (const point of imageDots) {
        const a = point.colors[index] || { r: 82, g: 118, b: 180, light: 0.2 + Math.sin(point.phase) * 0.1 };
        const b = point.colors[next] || a;
        const light = mix(a.light, b.light, morph);
        const proximity = pointer ? 1 - smooth(55, 260, Math.hypot(point.x - pointer.x, point.y - pointer.y)) : 0;
        const color = pointer ? proximity * (reducedMotion.matches ? 1 : 1 - smooth(1600, 4200, time - pointer.time)) : 0;
        let fill = '#747a85';
        if (color > 0.01) {
          const r = mix(a.r, b.r, morph);
          const g = mix(a.g, b.g, morph);
          const blue = mix(a.b, b.b, morph);
          const boost = 225 / Math.max(r, g, blue, 24);
          fill = `rgb(${Math.round(mix(116, r * boost, color))},${Math.round(mix(122, g * boost, color))},${Math.round(mix(133, blue * boost, color))})`;
        }
        const drift = reducedMotion.matches ? 0 : Math.sin(clock * 0.6 + point.phase) * light * 1.2;
        circle(point.x + drift, point.y + drift * 0.6, 0.4 + light * 1.65 + color * 0.9, alpha * (0.045 + light * 0.46 + color * 0.48), fill);
      }
    };

    const starPosition = (id, generation, count) => {
      // Shuffle occupied cells as well as their offsets, keeping each new field
      // evenly scattered while moving individual stars across the viewport.
      const slot = (id + generation * 47) % (count * 12);
      const seed = id + generation * 997;
      return {
        x: ((slot % 18 + random(seed + 30)) / 18) * width,
        y: ((Math.floor(slot / 18) + random(seed + 90)) / (count * 12 / 18)) * height,
      };
    };

    const drawStar = (point, radius, alpha, id, seed) => {
      // Small stars vary from charcoal to mid-gray, with clear contrast on white.
      const shade = Math.round(45 + random(id + 71) * 95);
      const color = `rgb(${shade},${shade},${shade})`;
      if (seed <= 0.7) {
        circle(point.x, point.y, radius, alpha, color);
        return;
      }
      const size = radius * 2.5;
      const waist = radius * 0.3;
      context.globalAlpha = alpha;
      context.fillStyle = color;
      context.beginPath();
      context.moveTo(point.x, point.y - size);
      context.quadraticCurveTo(point.x + waist, point.y - waist, point.x + size, point.y);
      context.quadraticCurveTo(point.x + waist, point.y + waist, point.x, point.y + size);
      context.quadraticCurveTo(point.x - waist, point.y + waist, point.x - size, point.y);
      context.quadraticCurveTo(point.x - waist, point.y - waist, point.x, point.y - size);
      context.fill();
    };

    const drawTrajectories = (time) => {
      const count = width < 760 ? 12 : PATHS.length;
      const lineAlpha = streams * (1 - smooth(0, 0.8, stars));
      const starTravel = 1 - (1 - stars) ** 3;
      const burst = Math.sin(stars * Math.PI) * (1 - stars);
      const generation = Math.floor(starClock / STAR_INTERVAL);
      const fade = smooth(STAR_INTERVAL - STAR_FADE, STAR_INTERVAL, starClock % STAR_INTERVAL);
      canvas.dataset.starGeneration = String(generation);
      for (let lane = 0; lane < count; lane += 1) {
        if (lineAlpha > 0.002) {
          context.beginPath();
          for (let step = 0; step <= 100; step += 1) {
            const p = trajectory(step / 100, lane, scroll);
            const point = ripplePoint({ x: p.x * width, y: p.y * height }, time, 1 - stars);
            if (step === 0) context.moveTo(point.x, point.y);
            else context.lineTo(point.x, point.y);
          }
          context.globalAlpha = lineAlpha * (lane % 3 === 0 ? 0.24 : 0.15);
          context.strokeStyle = '#737986';
          context.lineWidth = lane % 3 === 0 ? 0.95 : 0.7;
          context.stroke();
        }
        for (let node = 0; node < 12; node += 1) {
          const id = lane * 12 + node;
          const seed = random(id + 1);
          const direction = lane % 2 === 0 ? 1 : -1;
          const t = ((node / 12 + seed * 0.05 + clock * (0.011 + random(lane) * 0.007) * direction) % 1 + 1) % 1;
          const p = trajectory(t, lane, scroll);
          const start = { x: p.x * width, y: p.y * height };
          const destination = starPosition(id, generation, count);
          const dx = start.x - width * 0.5;
          const dy = start.y - height * 0.5;
          const distance = Math.hypot(dx, dy) || 1;
          const kick = burst * Math.min(width, height) * (0.2 + seed * 0.25);
          const position = ripplePoint({
            x: mix(start.x, destination.x, starTravel) + dx / distance * kick,
            y: mix(start.y, destination.y, starTravel) + dy / distance * kick,
          }, time, 1 - stars);
          const radius = mix(1.2 + seed ** 2 * 2.7, 0.65 + seed ** 2 * 1.3, stars);
          const [r, g, b] = COLORS[id % COLORS.length];
          if (stars < 1) {
            circle(position.x, position.y, radius, streams * (1 - stars) * (0.38 + seed * 0.3), `rgb(${r},${g},${b})`, node % 7 === 0);
          }
          if (stars > 0 && id % 4 !== 0) {
            const alpha = streams * stars * (0.72 + seed * 0.26);
            drawStar(position, radius, alpha * (1 - fade), id, seed);
            if (fade > 0) drawStar(starPosition(id, generation + 1, count), radius, alpha * fade, id, seed);
          }
        }
      }
    };

    const draw = (time) => {
      frame = 0;
      if (disposed || document.hidden) return;
      // Only the visible star scene cycles continuously; other scenes settle.
      if (lastTime && time - lastTime < 30) { frame = requestAnimationFrame(draw); return; }
      const realElapsed = lastTime ? time - lastTime : 32;
      const elapsed = clamp(realElapsed, 1, 64);
      lastTime = time;
      const amount = reducedMotion.matches ? 1 : 1 - Math.exp(-elapsed / 180);
      streams = mix(streams, targetStreams, amount);
      stars = mix(stars, targetStars, amount);
      scroll = mix(scroll, targetScroll, amount);
      if (Math.abs(streams - targetStreams) < 0.001) streams = targetStreams;
      if (Math.abs(stars - targetStars) < 0.001) stars = targetStars;
      if (Math.abs(scroll - targetScroll) < 0.0001) scroll = targetScroll;
      ripples = ripples.filter((ripple) => time - ripple.started < RIPPLE_DURATION);
      const moving = !reducedMotion.matches && time < activeUntil;
      if (moving) clock += elapsed * 0.001 * smooth(0, 650, activeUntil - time);
      const cyclingStars = !reducedMotion.matches && stars === 1 && targetStars === 1;
      if (cyclingStars) starClock += realElapsed;
      else if (stars === 0 || reducedMotion.matches) starClock = 0;
      context.clearRect(0, 0, width, height);
      if (streams < 0.999) drawImages(time, 1 - streams);
      if (streams > 0.001) drawTrajectories(time);
      const transitioning = streams !== targetStreams || stars !== targetStars || scroll !== targetScroll;
      if (!reducedMotion.matches && (moving || cyclingStars || transitioning || ripples.length)) frame = requestAnimationFrame(draw);
      else lastTime = 0;
    };

    const schedule = () => {
      if (!frame && !document.hidden && !disposed) frame = requestAnimationFrame(draw);
    };
    const resize = () => {
      const changed = width !== canvas.clientWidth || height !== canvas.clientHeight;
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
      }
      publicationsTop = (document.getElementById('publications')?.getBoundingClientRect().top || 0) + window.scrollY;
      aboutTop = (document.getElementById('about')?.getBoundingClientRect().top || 0) + window.scrollY;
      scrollRange = Math.max(1, document.documentElement.scrollHeight - height);
      if (changed) buildImageDots();
      readScroll();
      schedule();
    };
    const onScroll = () => {
      readScroll();
      activeUntil = performance.now() + 4800;
      schedule();
    };
    const onPointerMove = (event) => {
      if (!finePointer.matches || event.pointerType === 'touch' || document.hidden) return;
      const now = performance.now();
      if (now - lastPointerTime < 60) return;
      pointer = { x: event.clientX, y: event.clientY, time: now };
      const previous = ripples[ripples.length - 1];
      const moved = !previous || Math.hypot(pointer.x - previous.x, pointer.y - previous.y) >= 8;
      if (!reducedMotion.matches && streams > 0.001 && stars < 1 && moved) {
        ripples = [...ripples.slice(-23), { ...pointer, started: now }];
      }
      lastPointerTime = now;
      activeUntil = now + 4800;
      schedule();
    };
    const onPointerLeave = () => { pointer = null; schedule(); };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
      ripples = [];
    };
    const onMotionChange = () => {
      stop();
      activeUntil = 0;
      clock = 0;
      starClock = 0;
      pointer = null;
      readScroll();
      schedule();
    };
    const onVisibilityChange = () => {
      if (document.hidden) { stop(); pointer = null; }
      else { activeUntil = performance.now() + 4800; resize(); }
    };
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(canvas);
    resizeObserver?.observe(document.getElementById('main') || document.body);
    resize();
    streams = targetStreams;
    stars = targetStars;
    scroll = targetScroll;
    Promise.all(IMAGES.map(loadSample)).then((loaded) => {
      if (disposed) return;
      samples = loaded.filter(Boolean);
      canvas.dataset.images = String(samples.length);
      buildImageDots();
      schedule();
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);
    reducedMotion.addEventListener('change', onMotionChange);

    return () => {
      disposed = true;
      stop();
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      reducedMotion.removeEventListener('change', onMotionChange);
    };
  }, []);

  return <canvas ref={canvasRef} className="trajectory-background" aria-hidden="true" />;
}
